import React from "react";

import { Title } from "~/components";
import { usePageTitle } from "~/shared";

export const PrivacyPractices = () => {
  usePageTitle("Notice of Privacy Practices");

  return (
    <main className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 text-center">
          <Title className="mb-2 text-2xl font-bold text-gray-900 md:text-3xl">
            Elektra Health Notice of Privacy Practices
          </Title>
          <p className="text-sm text-gray-500">
            <strong>Last modified:</strong> September 5, 2025
          </p>
        </div>

        <div className="space-y-8">
          {/* Introduction Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Introduction
            </h2>
            <p className="mb-4 text-gray-700">
              This Notice describes how information about you may be used and
              disclosed and how you can get access to this information. Please
              review it carefully.
            </p>
            <p className="mb-4 text-gray-700">
              This Notice of Privacy Practices (the "Notice") tells you about
              the ways we may use and disclose your protected health information
              ("PHI") and your rights and our obligations regarding the use and
              disclosure of your PHI. "We" refers to, and this Notice applies to
              the trained and licensed personnel working through the practices
              with which My Elektra, Inc. contracts, including but not limited
              to Electra Health Medical, P.C., Electra Health Medical Mass,
              P.C., Electra Women's Health Medical NJ, P.C., and/or Elektra
              Women's Health Medical, Inc. (collectively, the "Practice").
            </p>
            <p className="mb-4 text-gray-700">
              PHI is information about you, including demographic information,
              that may identify you and that relates to your past, present or
              future physical health or condition, treatment or payment for
              health care services. This Notice also describes your rights to
              access and control your PHI.
            </p>
          </section>

          {/* Our Obligations Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              1. Our Obligations
            </h2>
            <p className="mb-4 text-gray-700">
              We maintain the privacy of your PHI and notify affected
              individuals following a breach of unsecured PHI, in each case to
              the extent required by state and federal law. We provide you this
              Notice explaining our legal duties and privacy practices with
              respect to PHI about you.
            </p>
          </section>

          {/* How We May Use and Disclose PHI Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              2. How We May Use and Disclose PHI About You
            </h2>
            <p className="mb-4 text-gray-700">
              The following categories describe the different ways that we
              typically use and disclose PHI, the purposes for such uses and
              disclosures, and the reasons for such uses and disclosures.
            </p>

            <div className="space-y-6">
              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  For Treatment
                </h3>
                <p className="mb-4 text-gray-700">
                  We may use and disclose PHI about you to provide you with
                  health care treatment and related services, including
                  coordinating and managing your health care. We may disclose
                  PHI about you to physicians, nurses, other health care
                  providers and personnel who are providing or involved in
                  providing health care to you (both within and outside of the
                  Practice). For example, should your care require referral to a
                  pharmacy for the provision of prescription drugs, we may
                  provide that pharmacy with your PHI in order to aid the
                  pharmacist in his or her treatment of you.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  For Payment
                </h3>
                <p className="mb-4 text-gray-700">
                  We may use and disclose PHI about you so that we or may bill
                  and collect from you, an insurance company, or a third party
                  for the health care services we provide. This may also include
                  the disclosure of PHI to obtain prior authorization for
                  treatment and procedures from your insurance plan. For
                  example, we may send a claim for payment to your insurance
                  company, and that claim may have a code on it that describes
                  the services that have been rendered to you. If, however, you
                  pay for an item or service in full, out of pocket and request
                  that we not disclose to your health plan the PHI solely
                  relating to that item or service, as described more fully in
                  Section 7 of this Notice, we will follow that restriction on
                  disclosure unless otherwise required by law.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  For Health Care Operations
                </h3>
                <p className="mb-4 text-gray-700">
                  We may use and disclose PHI about you for our health care
                  operations. These uses and disclosures are necessary to
                  operate and manage our practice and to promote quality care.
                  For example, we may need to use or disclose your PHI in order
                  to assess the quality of care you receive or to conduct
                  certain cost management, business management, administrative,
                  or quality improvement activities or to provide information to
                  our insurance carriers.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Quality Assurance and Utilization Review
                </h3>
                <p className="mb-4 text-gray-700">
                  We may need to use or disclose your PHI for our internal
                  processes to assess and facilitate the provision of quality
                  care to our patients. We may need to use or disclose your PHI
                  to perform a review of the services we provide in order to
                  evaluate whether that the appropriate level of services is
                  received, depending on condition and diagnosis.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Credentialing and Peer Review
                </h3>
                <p className="mb-4 text-gray-700">
                  We may need to use or disclose your PHI in order for us to
                  review the credentials, qualifications and actions of our
                  health care providers.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Treatment Alternatives
                </h3>
                <p className="mb-4 text-gray-700">
                  We may use and disclose PHI to tell you about or recommend
                  possible treatment options or alternatives that we believe may
                  be of interest to you.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Appointment Reminders and Information about Health Related
                  Benefits and Services
                </h3>
                <p className="mb-4 text-gray-700">
                  We may use and disclose PHI, in order to contact you
                  (including, for example, contacting you by phone and leaving a
                  message on an answering machine) to provide appointment
                  reminders and other information. We may use and disclose PHI
                  to tell you about health-related benefits or services that we
                  believe may be of interest to you. See also the specific types
                  of communications noted above.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Vendors
                </h3>
                <p className="mb-4 text-gray-700">
                  There are some services (such as billing or legal services)
                  that may be provided to or on behalf of the Practice through
                  contracts with third parties, including My Elektra, Inc. When
                  these services are contracted, we may disclose your PHI to our
                  vendors so that they can perform the jobs we have asked them
                  to do. To protect your PHI, however, we require the vendors,
                  as business associates, to appropriately safeguard your
                  information.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  As Required by Law
                </h3>
                <p className="mb-4 text-gray-700">
                  We will disclose PHI about you when required to do so by
                  federal, state, or local law or regulations.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Other
                </h3>
                <p className="mb-4 text-gray-700">
                  Subject to applicable legal requirements, and where
                  appropriate for your medical care or required by law, we may
                  also use your PHI (i) to avert an imminent threat of injury to
                  health or safety, (ii) for organ donation purposes, (iii) for
                  research, (iv) to appropriate military authorities if you are
                  in the armed forces, (v) for workers' compensation programs,
                  (vi) for public health activities, (vii) for health oversight
                  activities, (viii) for other legal matters, (ix) for law
                  enforcement purposes, or (x) to coroners and medical
                  examiners.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Electronic Disclosures of PHI
                </h3>
                <p className="mb-4 text-gray-700">
                  Under the law of certain states, we are required to provide
                  notice to you if your PHI is subject to electronic disclosure.
                  This Notice serves as general notice that we may disclose your
                  PHI electronically for treatment, payment, or health care
                  operations or as otherwise authorized or required by state or
                  federal law.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  De-Identified Data
                </h3>
                <p className="mb-4 text-gray-700">
                  We may de-identify your PHI in accordance with applicable law,
                  such that it no longer can be used to identify you
                  individually. Once so de-identified, we may use the data alone
                  or in aggregated form for any purpose allowable by law.
                </p>
              </div>
            </div>
          </section>

          {/* Other Uses of PHI Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              3. Other Uses of PHI
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Authorizations
                </h3>
                <p className="mb-4 text-gray-700">
                  There are times we may need or want to use or disclose your
                  PHI for reasons other than those listed above, but to do so we
                  will need your prior authorization. Any uses or disclosures of
                  your PHI not described herein will require your specific
                  written authorization.
                </p>
              </div>
              <p className="mb-4 text-gray-700">
                If you provide us with written authorization to use or disclose
                your PHI for such other purposes, you may revoke that
                authorization in writing at any time. If you revoke your
                authorization, we will no longer use or disclose your PHI for
                the reasons covered by your written authorization. You
                understand that we are unable to take back any uses or
                disclosures we have already made in reliance upon your
                authorization, and that we are required to retain our records of
                the care that we provided to you.
              </p>
              <p className="text-gray-700">The Practice will not sell PHI.</p>
            </div>
          </section>

          {/* Alcohol and Drug Abuse Records Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              4. Special Treatment of Alcohol and Drug Abuse Records
            </h2>
            <p className="text-gray-700">
              If we were to receive health information about you from federally
              assisted alcohol or drug treatment programs, this information may
              be subject to special protection under state or federal law and we
              will not disclose this information without your authorization
              except where authorized or required by, and in full compliance
              with, federal or state law.
            </p>
          </section>

          {/* Reproductive Health Information Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              5. Special Protections for Reproductive Health Information
            </h2>
            <p className="mb-4 text-gray-700">
              Even if you do not receive reproductive health care or have not
              received such care, we are required to provide the following
              information:
            </p>
            <p className="mb-4 text-gray-700">
              We will never use or disclose your health information when the
              requested use or disclosure is for any of the following purposes:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>
                To conduct a criminal, civil, or administrative investigation
                for the mere act of you seeking, obtaining, providing, or
                facilitating reproductive health care;
              </li>
              <li>
                To impose criminal, civil, or administrative liability for the
                mere act of your seeking, obtaining, providing, or facilitating
                reproductive health care; or
              </li>
              <li>To identify you for either of the above purposes.</li>
            </ul>
            <p className="mb-4 text-gray-700">
              For example, we would not cooperate with a subpoena issued by a
              state in which abortion or gender-affirming care is illegal that
              is seeking your medical records in order to investigate your
              receipt of reproductive healthcare that you lawfully obtained.
            </p>
            <p className="mb-4 text-gray-700">
              We will not use or disclose your health information that is
              potentially related to reproductive health care for:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>Health oversight activities;</li>
              <li>Judicial and administrative proceedings;</li>
              <li>Law enforcement purposes; or to</li>
              <li>Coroners and medical examiners</li>
            </ul>
            <p className="mb-4 text-gray-700">
              UNLESS we receive a valid attestation from the person requesting
              the use or disclosure that their request is not related to the
              prohibited purposes listed above.
            </p>
            <p className="text-gray-700">
              For example, if we receive a subpoena requesting medical records
              of reproductive health care that you lawfully obtained that is
              accompanied by a valid attestation that the subpoena relates to
              investigating a doctor suspected of malpractice, we would be able
              to lawfully disclose your information.
            </p>
          </section>

          {/* Potential for Redisclosure Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              6. Potential for Redisclosure
            </h2>
            <p className="text-gray-700">
              We want you to be aware that when we disclose your information as
              described in this Notice, either with or without your
              authorization, it has the potential to be redisclosed by the
              person receiving the information, and the information may no
              longer subject to the protections we've described, or protected by
              the laws with which we comply.
            </p>
          </section>

          {/* Your Rights Regarding PHI Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              7. Your Rights Regarding PHI About You
            </h2>
            <p className="mb-4 text-gray-700">
              Certain laws and regulations provide you with certain rights
              regarding the PHI we have about you. The following is a summary of
              those rights.
            </p>

            <div className="space-y-6">
              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Right to Inspect and Copy
                </h3>
                <p className="mb-4 text-gray-700">
                  Under most circumstances, you have the right to request access
                  to, inspect and/or copy your PHI that we maintain in our
                  possession in a designated record set, which generally
                  includes your medical and billing records. If you request a
                  copy of your information, we may charge a fee for the costs of
                  copying, mailing, or certain supplies associated with your
                  request. The fee we may charge will be the amount allowed by
                  state law. In certain very limited circumstances allowed by
                  law, we may deny your request to review or copy your PHI. We
                  will provide any such denial in writing. If you are denied
                  access to PHI, you may request that the denial be reviewed.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Right to Amend
                </h3>
                <p className="mb-4 text-gray-700">
                  If you feel the PHI we have about you is incorrect or
                  incomplete, you may ask us to amend the information. In your
                  request, you must provide a reason as to why you want this
                  amendment. If we accept your request, we will notify you of
                  that in writing. We may deny your request for an amendment
                  under certain circumstances. If we deny your request, we will
                  notify you of that denial in writing, and provide you with an
                  opportunity to appeal.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Right to an Accounting of Disclosures
                </h3>
                <p className="mb-4 text-gray-700">
                  You have the right to request an accounting of certain
                  disclosures of your PHI that we have made, except for
                  disclosures which were made pursuant to an authorization, for
                  purposes of treatment, payment or health care operations, or
                  for certain other purposes. Your request must state a time
                  period, which may not be longer than six years. The first list
                  you request within a twelve-month period will be free. For
                  additional lists, we may charge you a reasonable fee for the
                  costs of providing the list. We will notify you of the cost
                  involved and you may choose to withdraw or modify your request
                  at that time before any costs are incurred.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Right to Request Restrictions
                </h3>
                <p className="mb-4 text-gray-700">
                  You have the right to request a restriction or limitation on
                  our use or disclosure of your PHI. Your request must be in
                  writing and state the specific restriction and to whom you
                  want the restriction to apply. We are not required to agree to
                  your request for a restriction or limitation, except if the
                  requested restriction is on a disclosure to a health plan for
                  a payment or health care operations purpose regarding a
                  service that has been paid in full out-of-pocket. If we do
                  agree, we will comply with your request unless the information
                  is needed to provide emergency treatment. In addition, there
                  are certain situations where we won't be able to agree to your
                  request, such as when we are required by law to use or
                  disclose your PHI.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Right to Request Confidential Communications
                </h3>
                <p className="mb-4 text-gray-700">
                  You have the right to request that we communicate with you
                  about medical matters in a certain way or at a certain
                  location. For example, you can ask that we only contact you
                  through a personal email address and not at work or,
                  conversely, only at work and not a personal email address. We
                  will use our best efforts to accommodate all reasonable
                  requests, but there are some requests with which we will not
                  be able comply. Your request must be in writing and specify
                  how and where you wish to be contacted.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Right to an Email or Paper Copy of This Notice
                </h3>
                <p className="mb-4 text-gray-700">
                  You have the right to a paper copy of this Notice, upon
                  request, even if you have previously requested its receipt
                  electronically. You may ask us to give you a copy of this
                  Notice at any time.
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  Right to Breach Notification
                </h3>
                <p className="mb-4 text-gray-700">
                  In certain instances, we may be obligated to notify you (and
                  potentially other parties) if we become aware that your PHI
                  has been improperly disclosed or otherwise subject to a
                  "breach" as defined in and/or required by applicable law.
                </p>
              </div>
            </div>
          </section>

          {/* Changes to this Notice Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              8. Changes to this Notice
            </h2>
            <p className="text-gray-700">
              We reserve the right to change this Notice at any time, along with
              our privacy policies and practices. We reserve the right to make
              the revised or changed Notice effective for PHI we already have
              about you as well, as any information we receive in the future. We
              will post a copy of the current notice, along with an announcement
              that changes have been made, as applicable, on our website.
            </p>
          </section>

          {/* Complaints Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              9. Complaints
            </h2>
            <p className="mb-4 text-gray-700">
              If you believe that your privacy rights as described in this
              Notice have been violated, you may file a complaint with the
              Practice at: clinicalops@elektrahealth.com.
            </p>
            <p className="mb-4 text-gray-700">
              The Practice will not retaliate against any individual who files a
              complaint. You may also file a complaint with the Secretary of the
              Department of Health and Human Services.
            </p>
            <p className="text-gray-700">
              In addition, if you have any questions about this Notice, please
              contact clinicalops@elektrahealth.com.
            </p>
          </section>

          {/* Acknowledgement of Receipt Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              10. Acknowledgement of Receipt of Notice of Privacy Practices
            </h2>
            <p className="text-gray-700">
              By agreeing to use these Services, you acknowledge that you have
              received or been given an opportunity to receive this Notice.
            </p>
          </section>
        </div>

        <div className="mt-8 rounded-lg bg-white p-6 text-center">
          <p className="mb-2 text-sm text-gray-500">
            By using Elektra Health's services, you acknowledge that you have
            read and understood this Notice of Privacy Practices.
          </p>
          <p className="text-sm text-gray-500">
            <strong>Last modified:</strong> September 5, 2025
          </p>
        </div>
      </div>
    </main>
  );
};
